import { useState, useRef, useCallback, useEffect } from 'react';
import { getProtocol, splitFrames } from '../utils/weightScaleParser';

/**
 * useWeightScale — reads a live weight from an RS-232 weighing scale via the
 * Web Serial API (Chrome / Edge). Self-contained: used ONLY by the POS weight
 * scale plugin when the `pos_weight_scale` module is enabled. No effect on the
 * rest of the POS.
 *
 * Returns connection state, the live weight (kg, after software tare), a
 * stability flag, recent raw frames (for the debug view), and controls.
 */
const SETTINGS_KEY = 'amal_pos_scale_settings';
const loadSettings = () => { try { return JSON.parse(localStorage.getItem(SETTINGS_KEY)) || {}; } catch { return {}; } };

export default function useWeightScale(enabled) {
  const supported = typeof navigator !== 'undefined' && 'serial' in navigator;
  const [connected, setConnected] = useState(false);
  const [liveWeight, setLiveWeight] = useState(0);
  const [stable, setStable] = useState(false);
  const [error, setError] = useState('');
  const [rawFrames, setRawFrames] = useState([]);
  const s0 = loadSettings();
  const [baudRate, setBaudRate] = useState(s0.baudRate || 9600);
  const protocolKey = s0.protocol || 'generic';

  const portRef = useRef(null);
  const readerRef = useRef(null);
  const keepReadingRef = useRef(false);
  const tareRef = useRef(0);
  const historyRef = useRef([]);
  const bufferRef = useRef('');

  useEffect(() => { try { localStorage.setItem(SETTINGS_KEY, JSON.stringify({ baudRate, protocol: protocolKey })); } catch { /* ignore */ } }, [baudRate, protocolKey]);

  // Steadiness check when the scale doesn't declare ST/US itself.
  const computeStable = (w) => {
    const now = Date.now();
    const hist = historyRef.current;
    hist.push({ t: now, w });
    while (hist.length && now - hist[0].t > 700) hist.shift();
    if (hist.length < 3) return false;
    const ws = hist.map(h => h.w);
    return (Math.max(...ws) - Math.min(...ws)) <= 0.003;
  };

  const readLoop = useCallback(async (port) => {
    const proto = getProtocol(protocolKey);
    const decoder = new TextDecoder();
    while (keepReadingRef.current && port.readable) {
      let reader;
      try {
        reader = port.readable.getReader();
        readerRef.current = reader;
        while (keepReadingRef.current) {
          const { value, done } = await reader.read();
          if (done) break;
          if (!value) continue;
          bufferRef.current += decoder.decode(value, { stream: true });
          const { lines, rest } = splitFrames(bufferRef.current);
          bufferRef.current = rest;
          for (const line of lines) {
            setRawFrames(prev => [...prev, line].slice(-20));
            const parsed = proto.parse(line);
            if (parsed) {
              const w = Math.max(0, Math.round((parsed.weightKg - tareRef.current) * 1000) / 1000);
              setLiveWeight(w);
              setStable(parsed.stable != null ? parsed.stable : computeStable(w));
            }
          }
        }
      } catch (e) {
        if (keepReadingRef.current) setError('Scale read error: ' + (e?.message || e));
        break;
      } finally {
        try { reader && reader.releaseLock(); } catch { /* ignore */ }
      }
    }
  }, [protocolKey]);

  const connect = useCallback(async () => {
    setError('');
    if (!supported) { setError('Web Serial not supported here — use Chrome or Edge.'); return; }
    try {
      const port = await navigator.serial.requestPort();
      await port.open({ baudRate: Number(baudRate) || 9600, dataBits: 8, stopBits: 1, parity: 'none' });
      portRef.current = port;
      keepReadingRef.current = true;
      setConnected(true);
      readLoop(port);
    } catch (e) {
      if (e && e.name === 'NotFoundError') return; // user closed the port picker
      setError('Could not connect to scale: ' + (e?.message || e));
    }
  }, [supported, baudRate, readLoop]);

  const disconnect = useCallback(async () => {
    keepReadingRef.current = false;
    try { await readerRef.current?.cancel(); } catch { /* ignore */ }
    try { readerRef.current?.releaseLock(); } catch { /* ignore */ }
    try { await portRef.current?.close(); } catch { /* ignore */ }
    portRef.current = null; readerRef.current = null;
    setConnected(false); setLiveWeight(0); setStable(false);
  }, []);

  // Software tare: zero the current reading (no command sent to the scale).
  const tare = useCallback(() => { tareRef.current += liveWeight; }, [liveWeight]);
  const clearTare = useCallback(() => { tareRef.current = 0; }, []);

  // Clean up the port if the plugin is turned off or the screen unmounts.
  useEffect(() => {
    return () => {
      keepReadingRef.current = false;
      try { readerRef.current?.cancel(); } catch { /* ignore */ }
      try { portRef.current?.close(); } catch { /* ignore */ }
    };
  }, []);

  return {
    supported, connected, liveWeight, stable, error, rawFrames,
    baudRate, setBaudRate, connect, disconnect, tare, clearTare,
    // when enabled is false the consumer simply won't render anything
    enabled: !!enabled,
  };
}
